import { useCallback, useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useRequests from "../../hooks/http";
import { parseDetail } from "../../utils/errors";

export default function EmailVerification() {
  const { t } = useTranslation();
  const { get, post } = useRequests();

  const [email, setEmail] = useState("");
  const [verified, setVerified] = useState(true);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    const { data, status } = await get("/platform/self/email");
    if (status === 200) {
      setEmail(data.email);
      setVerified(data.verified);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const request = async () => {
    setError("");
    setSuccess("");
    const { data, status } = await post("/platform/self/email/verify/request");
    if (status === 200) {
      setSent(true);
      return;
    }
    setError(parseDetail(data?.detail, t("settings.emailVerification.sendFailed")));
  };

  const confirm = async () => {
    setError("");
    const { data, status } = await post("/platform/self/email/verify", null, {
      code,
    });
    if (status === 200) {
      setVerified(true);
      setSent(false);
      setCode("");
      setSuccess(t("settings.emailVerification.verified"));
      return;
    }
    setError(parseDetail(data?.detail, t("settings.emailVerification.confirmFailed")));
  };

  if (verified) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Email: {email}
        </Typography>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleIcon />}
          label={t("settings.emailVerification.verifiedLabel")}
        />
        {success && (
          <Typography variant="caption" color="success.main">
            {success}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("settings.emailVerification.unverified", { email })}
      </Typography>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {error}
        </Typography>
      )}

      {!sent ? (
        <Button size="small" variant="outlined" onClick={request}>
          {t("settings.emailVerification.send")}
        </Button>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label={t("settings.emailVerification.codeLabel")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
          />
          <Button size="small" variant="contained" onClick={confirm} disabled={!code}>
            {t("settings.emailVerification.confirm")}
          </Button>
          <Button size="small" onClick={request}>
            {t("settings.emailVerification.resend")}
          </Button>
        </Stack>
      )}
    </Alert>
  );
}
